import { isShortcutsDisabledAtom } from '@/components/layout/ShortcutsHelper/shortcutsAtom';
import {
  clearCreditAllowanceRefusal,
  nudgeCreditAllowanceRefusal,
  rememberCreditAllowanceRefusal,
  shouldSkipCreditAllowanceFollowUpSend,
} from '@/components/ui/agent-credit-allowance-banner/creditAllowanceRefusalAtom';
import { IS_CHAT_ATTACHMENTS_ENABLED } from '@/features/flags/legacy-flags';
import { getMergeConnectorLabel } from '@/features/integrations-connected-apps/merge-agent-handler/connectors';
import { getNangoProviderLabel } from '@/features/integrations-connected-apps/nango/capabilities';
import { NANGO_ALLOWED_INTEGRATIONS } from '@/features/integrations-connected-apps/nango/providers';
import { useReferenceableLibraryDocuments } from '@/features/workflows/components/useWorkflowContextDocumentUpload';
import { useWorkflows } from '@/features/workflows/hooks';
import {
  cancelChatAttachmentUpload,
  useMutateDeleteChatAttachment,
  useMutateUploadChatAttachment,
} from '@/hooks/mutation/useMutateUploadChatAttachment';
import { useChatFileUpload } from '@/hooks/useChatFileUpload';
import { formatChatUploadErrorMessage } from '@/hooks/useChatFileUpload.messages';
import { hotkeysList } from '@/lists/hotkeys';
import {
  useProjectsComposerChatSessionCore,
  type CreditAllowanceRefusalCallbacks,
  type ProjectsComposerChatSessionArgs,
  type UseStopGeneratingHotkey,
} from '@heidi/agents';
import {
  useComposerAttachmentUploadAdapter,
  type ChatAttachmentUploadPorts,
} from '@heidi/agents/data';
import { useAtomValue } from 'jotai';
import { useHotkeys } from 'react-hotkeys-hook';
import { useIntl } from 'react-intl';
import { useAvailableWorkflowSkillsWithWorkflows } from './useAvailableWorkflowSkills';
import { useLinkableContextItems } from './useLinkableContextItems';

// Module-level so the reference is stable: useHotkeys compares options by
// reference for functions, and a fresh one re-registers the hotkey every
// render — which re-renders the provider and loops (React #185).
const isEscAlreadyClaimed = (event: KeyboardEvent) => event.defaultPrevented;

/** Chat's attachment upload stack, which the Work-home upload adapter wraps. */
const chatAttachmentUploadPorts: ChatAttachmentUploadPorts = {
  useUploadAttachment: useMutateUploadChatAttachment,
  useDeleteAttachment: useMutateDeleteChatAttachment,
  cancelUpload: cancelChatAttachmentUpload,
};

/** The refusal the credit-allowance banner reads, kept in the app's atom. */
const creditAllowanceRefusal: CreditAllowanceRefusalCallbacks = {
  remember: rememberCreditAllowanceRefusal,
  clear: clearCreditAllowanceRefusal,
  nudge: nudgeCreditAllowanceRefusal,
  shouldSkipFollowUpSend: shouldSkipCreditAllowanceFollowUpSend,
};

/** The Esc stop, registered in the app's hotkey list and its scopes. */
const useStopGeneratingHotkey: UseStopGeneratingHotkey = (onStop, enabled) => {
  useHotkeys(
    hotkeysList.stopGenerating.hotkeys,
    () => onStop(),
    {
      scopes: hotkeysList.stopGenerating.scopes,
      preventDefault: true,
      ignoreEventWhen: isEscAlreadyClaimed,
      enabled,
    },
    [onStop]
  );
};

/** A Nango provider by its label; anything else by Merge's connector catalogue. */
const resolveConnectorLabel = (connectorId: string): string => {
  const nangoKeys = new Set<string>(NANGO_ALLOWED_INTEGRATIONS);
  return nangoKeys.has(connectorId)
    ? getNangoProviderLabel(connectorId)
    : getMergeConnectorLabel(connectorId);
};

/**
 * Binds the Work chat composer session to what the app owns: Chat's pre-upload
 * lifecycle over the Work upload adapter, the linkable rows and the library
 * documents, the routines and their skills, the shortcuts switch, the hotkey
 * registry, the credit-allowance refusal and the connected-app labels.
 */
export const useProjectsComposerChatSession = (args: ProjectsComposerChatSessionArgs) => {
  const intl = useIntl();
  // Read at render, as the composer always read it. Until PostHog resolves it the
  // read is `undefined`: the session stages files in the browser, and the upload
  // hook keeps its own default for an unset option.
  const attachmentsFlag = IS_CHAT_ATTACHMENTS_ENABLED();
  const uploadAdapter = useComposerAttachmentUploadAdapter(
    args.projectId,
    chatAttachmentUploadPorts
  );
  const uploads = useChatFileUpload({
    adapter: uploadAdapter,
    enableUpload: attachmentsFlag,
    formatErrorMessage: (errorCode) => formatChatUploadErrorMessage(intl, errorCode),
  });
  const isShortcutsDisabled = useAtomValue(isShortcutsDisabledAtom);
  const linkableContextItems = useLinkableContextItems({ limit: 50 });
  const lockedSessionPatientScope = args.lockedSessionPatientScope ?? null;
  const { sessionItems: lockedPatientSessionItems } = useLinkableContextItems({
    limit: lockedSessionPatientScope ? Number.POSITIVE_INFINITY : 0,
    patientScope: lockedSessionPatientScope,
  });
  const referenceableLibraryDocuments = useReferenceableLibraryDocuments({ limit: 50 });
  const availableSkills = useAvailableWorkflowSkillsWithWorkflows();
  const { data: workflowsData } = useWorkflows();

  return useProjectsComposerChatSessionCore({
    ...args,
    attachmentsEnabled: Boolean(attachmentsFlag),
    uploads,
    linkableContextItems,
    lockedPatientSessionItems,
    referenceableLibraryDocuments,
    availableSkills,
    routines: workflowsData?.workflows,
    isShortcutsDisabled,
    useStopGeneratingHotkey,
    creditAllowanceRefusal,
    resolveConnectorLabel,
  });
};
